import { Alert, Card, CardGroup, DropdownControl, Fieldset, Form, Icon, TextInputControl } from '@ver-uds/uswds-react';
import React, { useEffect, useState } from 'react';
import { Button } from '@ver-uds/react';
import { useForm } from 'react-hook-form';
import { useDashboard } from '../../../context/Dashboard/DashboardContext';
import { UserProfileFormValues } from '../../../constants/Dashboard';
import useDashboardApi from '../../../services/DashboardApi';

const FORM_GROUP_CSS =
  'desktop:grid-col-4 tablet:grid-col-6 mobile:grid-col-12 tablet:padding-right-4 display-flex flex-column flex-justify-end margin-top-0';

const stringOrUndefined = (str: string | undefined): string | undefined => (str?.length ? str : undefined);

const RequiredLabel = (): JSX.Element => (
  <span style={{ opacity: 0.6, fontSize: '0.95rem', fontWeight: 'normal' }}>(required)</span>
);

export type DashboardEditUserProfileCardProps = {
  onFinishedEditing: (submitted?: boolean) => void;
};

const DashboardEditUserProfileCard = ({ onFinishedEditing }: DashboardEditUserProfileCardProps): JSX.Element => {
  const { profile, hasProfileError } = useDashboard();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserProfileFormValues>({
    defaultValues: {
      firstName: profile?.firstName ?? '',
      middleInitial: profile?.middleInitial ?? '',
      lastName: profile?.lastName ?? '',
      emailAddress: profile?.email ?? '',
      phoneNumber: profile?.phoneNumber?.number ?? '',
      phoneExt: profile?.phoneNumber?.extension ?? '',
    },
  });
  const dashboardApi = useDashboardApi();

  const [allEmails, setAllEmails] = useState<string[]>([]);
  const [hasServerError, setHasServerError] = useState(false);

  const handleSubmitForm = handleSubmit(async (data: UserProfileFormValues): Promise<void> => {
    await dashboardApi
      .putSharedProfile({
        person: {
          email: data.emailAddress,
          firstName: data.firstName,
          middleInitial: stringOrUndefined(data.middleInitial),
          lastName: data.lastName,
          phoneNumber: {
            number: data.phoneNumber,
            extension: stringOrUndefined(data.phoneExt),
          },
        },
      })
      .then(() => {
        setHasServerError(false);
        onFinishedEditing(true);
      })
      .catch(() => {
        setHasServerError(true);
      });
  });

  useEffect(() => {
    const emails = profile?.allEmails?.slice() ?? [];
    if (emails.length === 0 && profile?.email) {
      emails.push(profile.email);
    }
    setAllEmails(emails);
    reset({ emailAddress: profile?.email ?? emails[0] });
  }, [profile?.allEmails, profile?.email, reset]);

  return (
    <CardGroup>
      <Card className="user-profile-card">
        <Card.Body>
          <div className="grid-row margin-bottom-2">
            <h2 className="grid-row flex-align-center">
              <Icon name="account_circle" className="width-5 height-5" />
              <div className="margin-left-105">User Profile</div>
            </h2>
          </div>

          {hasProfileError && (
            <Alert status="warning" slim className="margin-top-3 margin-bottom-3">
              <Alert.Text>User profile information could not be displayed at this time.</Alert.Text>
            </Alert>
          )}

          <div className="grid-row">
            If you want to update your email to an address that is not available as a selection below, you need to first
            add it to your Login.gov account.
          </div>

          <Form className="maxw-none" onSubmit={handleSubmitForm} noValidate>
            <Fieldset className="grid-row">
              <div className={FORM_GROUP_CSS}>
                <DropdownControl
                  id="emailAddress"
                  label="Email Address"
                  required
                  requiredLabel={<RequiredLabel />}
                  disabled={allEmails.length === 1}
                  aria-label={
                    allEmails.length === 1 ? 'Dropdown disabled as only one email account is available' : undefined
                  }
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('emailAddress', { required: 'Email address is required.' })}
                  errorMessage={errors?.emailAddress?.message}
                  errorId="emailAddress-error"
                >
                  {allEmails?.map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </DropdownControl>
              </div>
            </Fieldset>

            <Fieldset className="grid-row">
              <div className={FORM_GROUP_CSS}>
                <TextInputControl
                  id="firstName"
                  label="First Name"
                  data-testid="first-name-input"
                  required
                  requiredLabel={<RequiredLabel />}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('firstName', {
                    required: 'First name is required.',
                    pattern: {
                      value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                    maxLength: { value: 25, message: 'This field must not exceed 25 characters.' },
                  })}
                  errorMessage={errors?.firstName?.message}
                  errorId="firstName-error"
                  type="text"
                />
              </div>

              <div className={FORM_GROUP_CSS}>
                <TextInputControl
                  id="middleInitial"
                  label="Middle Initial"
                  data-testid="middle-initial-input"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('middleInitial', {
                    maxLength: {
                      value: 1,
                      message: 'This field must not exceed 1 character.',
                    },
                    pattern: {
                      value: /^[a-zA-Z]+$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                  })}
                  errorMessage={errors?.middleInitial?.message}
                  errorId="middleInitial-error"
                  type="text"
                />
              </div>

              <div className={FORM_GROUP_CSS}>
                <TextInputControl
                  id="lastName"
                  label="Last Name"
                  data-testid="last-name-input"
                  required
                  requiredLabel={<RequiredLabel />}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('lastName', {
                    required: 'Last name is required.',
                    pattern: {
                      value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                    maxLength: { value: 25, message: 'This field must not exceed 25 characters.' },
                  })}
                  errorMessage={errors?.lastName?.message}
                  errorId="lastName-error"
                  type="text"
                />
              </div>
            </Fieldset>

            <Fieldset className="grid-row">
              <div className={FORM_GROUP_CSS}>
                <TextInputControl
                  id="phoneNumber"
                  label="Phone Number"
                  data-testid="phone-number-input"
                  hint="10 digits, numbers only"
                  required
                  requiredLabel={<RequiredLabel />}
                  minLength={10}
                  maxLength={10}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('phoneNumber', {
                    required: 'Phone number is required.',
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'This field must contain 10 digits.',
                    },
                    validate: (value: string) => {
                      if (!/^(?!0|1)/.exec(value)) {
                        return 'Please enter valid phone number without 0 or 1 as the first digit.';
                      }
                      return true;
                    },
                  })}
                  errorMessage={errors?.phoneNumber?.message}
                  errorId="phoneNumber-error"
                  type="text"
                />
              </div>

              <div className={FORM_GROUP_CSS}>
                <TextInputControl
                  id="phoneExt"
                  label="Phone Number Extension"
                  data-testid="phone-ext-input"
                  hint="Up to 6 digits, numbers only"
                  maxLength={6}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('phoneExt', {
                    pattern: {
                      value: /^\d+$/,
                      message: 'This field must only contain digits.',
                    },
                    maxLength: {
                      value: 6,
                      message: 'This field must not exceed 6 digits.',
                    },
                  })}
                  errorMessage={errors?.phoneExt?.message}
                  errorId="phoneExt-error"
                  type="text"
                />
              </div>
            </Fieldset>

            {hasServerError && (
              <Alert status="error" slim className="margin-top-3">
                <Alert.Text>
                  Your profile is unable to be updated at this time. Please wait a few minutes and try again.
                </Alert.Text>
              </Alert>
            )}

            <div className="grid-row margin-top-1 margin-bottom-3">
              <Button variant="secondary" className="user-save-button" onClick={(): void => onFinishedEditing()}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="user-save-button">
                Save
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </CardGroup>
  );
};

export default DashboardEditUserProfileCard;
